'use client';

// Since QueryClientProvider relies on useContext under the hood, we have to put 'use client' on top
import { ReactNode } from 'react';

import { QueryClient, QueryClientProvider as TanstackQueryClientProvider, isServer } from '@tanstack/react-query';

function makeQueryClient() {
    return new QueryClient({
        defaultOptions: {
            queries: {
                refetchOnWindowFocus: false,
            },
        },
    });
}

let browserQueryClient: QueryClient | undefined = undefined;

function getQueryClient() {
    if (isServer) {
        // Server: always make a new query client
        return makeQueryClient();
    } else {
        // Browser: make a new query client if we don't already have one
        // This is very important, so we don't re-make a new client if React
        // suspends during the initial render. This may not be needed if we
        // have a suspense boundary BELOW the creation of the query client
        browserQueryClient ??= makeQueryClient();
        return browserQueryClient;
    }
}

interface QueryClientProviderProps {
    children: ReactNode;
}

export function QueryClientProvider({ children }: Readonly<QueryClientProviderProps>) {
    // NOTE: Avoid useState when initializing the query client if you don't
    //       have a suspense boundary between this and the code that may
    //       suspend because React will throw away the client on the initial
    //       render if it suspends and there is no boundary
    const queryClient = getQueryClient();

    return <TanstackQueryClientProvider client={queryClient}>{children}</TanstackQueryClientProvider>;
}
